import { useSenderStore } from '../store/senderStore';
import { DropZone } from '../components/DropZone';
import { ShareLink } from '../components/ShareLink';
import { TransferProgress } from '../components/TransferProgress';
import { PhaseBadge } from '../components/Badges';
import { formatBytes, formatTimeUntil } from '../lib/util/format';

export function HomePage() {
  const {
    phase,
    file,
    meta,
    shareUrl,
    expiresAt,
    stats,
    error,
    creating,
    createTransfer,
    cancel,
    reset,
  } = useSenderStore();

  const active = shareUrl !== null;

  return (
    <div className="space-y-6">
      {!active && (
        <section className="space-y-6">
          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
              Send big files, straight from your browser
            </h1>
            <p className="mx-auto max-w-xl text-slate-400">
              No accounts, no uploads, no size caps. Your file streams directly to the recipient
              over an encrypted peer-to-peer link. Keep this tab open until they finish.
            </p>
          </div>
          <DropZone onFile={createTransfer} />
          {creating && <p className="text-center text-sm text-slate-400">Creating transfer…</p>}
          {error && <ErrorNote message={error} />}
        </section>
      )}

      {active && (
        <section className="space-y-5 rounded-2xl border border-white/10 bg-ink-900/50 p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-slate-100">
                {meta?.name ?? file?.name}
              </h2>
              <p className="text-sm text-slate-400">
                {formatBytes(meta?.size ?? file?.size ?? 0)}
                {expiresAt && <> · link expires {formatTimeUntil(expiresAt)}</>}
              </p>
            </div>
            <PhaseBadge phase={phase} />
          </div>

          {shareUrl && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-300">Share this link with the recipient</p>
              <ShareLink url={shareUrl} />
              {phase === 'waiting-for-peer' && (
                <p className="text-xs text-amber-300/90">
                  Waiting for the recipient to open the link. Keep this tab open.
                </p>
              )}
            </div>
          )}

          {(phase === 'transferring' ||
            phase === 'verifying' ||
            phase === 'complete' ||
            phase === 'connecting') && <TransferProgress stats={stats} />}

          {phase === 'complete' && (
            <p className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
              Transfer complete. The recipient has the file — you can close this tab.
            </p>
          )}

          {error && <ErrorNote message={error} />}

          <div className="flex gap-3">
            {phase !== 'complete' && phase !== 'error' && phase !== 'cancelled' && (
              <button
                onClick={cancel}
                className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-300 transition hover:bg-white/5"
              >
                Cancel
              </button>
            )}
            <button
              onClick={reset}
              className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-400"
            >
              Send another file
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <p className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{message}</p>
  );
}
